import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Transaction, TransactionType } from '../../types';
import { API_ROUTES } from '../../lib/constants';
import axios from 'axios';
import { operationsService } from '../../services/api/operationsService';
import { reportService, ReportTransaction } from '../../services/api/reportService';

interface TransactionsState {
  transactions: Transaction[];
  filteredTransactions: Transaction[];
  currentTransaction: Transaction | null;
  lastMovements: any[];
  // Reporte paginado desde /get-report-data
  reportData: ReportTransaction[];
  reportPage: number;
  reportLastPage: number;
  reportTotals: { solds: number; reverse: number; error: number; amount: string; commission: string } | null;
  isLoadingReport: boolean;
  filter: {
    type?: TransactionType;
    provider?: string;
    phoneNumber?: string;
    startDate?: string;
    endDate?: string;
  };
  isLoading: boolean;
  error: string | null;
}

// Estado inicial
const initialState: TransactionsState = {
  transactions: [],
  filteredTransactions: [],
  currentTransaction: null,
  lastMovements: [],
  reportData: [],
  reportPage: 1,
  reportLastPage: 1,
  reportTotals: null,
  isLoadingReport: false,
  filter: {},
  isLoading: false,
  error: null,
};

// ─── Thunks (deben definirse ANTES del slice para evitar TDZ en extraReducers) ─

export const fetchTransactions = createAsyncThunk(
  'transactions/fetchTransactions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(API_ROUTES.TRANSACCIONES);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data.message || 'Error al obtener transacciones');
      }
      return rejectWithValue('Error al conectar con el servidor');
    }
  }
);

export const fetchTransactionById = createAsyncThunk(
  'transactions/fetchTransactionById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_ROUTES.TRANSACCIONES}/${id}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data.message || 'Error al obtener detalles de la transacción');
      }
      return rejectWithValue('Error al conectar con el servidor');
    }
  }
);

export const fetchLastMovements = createAsyncThunk(
  'transactions/fetchLastMovements',
  async (_, { rejectWithValue }) => {
    try {
      const response = await operationsService.getLastMovements();
      if (response && response.success && response.data) {
        if (!Array.isArray(response.data)) return [];

        const processedData = response.data
          .map((movement: any) => {
            if (!movement) return null;
            try {
              const pdfId = movement.xid || movement.id;
              return { ...movement, url: `https://kaoxet.net/pdf/recharges/recharge-${pdfId}.pdf` };
            } catch {
              return movement;
            }
          })
          .filter(Boolean);

        const movementsByXid: { [key: string]: any[] } = {};
        processedData.forEach((m: any) => {
          if (m.xid) {
            if (!movementsByXid[m.xid]) movementsByXid[m.xid] = [];
            movementsByXid[m.xid].push(m);
          }
        });

        return Object.values(movementsByXid).flatMap((group) => {
          if (group.length === 1) return group;
          const reversed = group.find((m) => m.status === 3);
          return reversed ? [reversed] : group;
        });
      }
      return rejectWithValue(response?.message || 'Error al obtener los últimos movimientos');
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data.message || 'Error al obtener últimos movimientos');
      }
      return rejectWithValue((error as Error).message || 'Error al conectar con el servidor');
    }
  }
);

/**
 * Carga el reporte paginado desde /get-report-data.
 * El backend espera fechas en formato d/m/Y y type (1=Recarga,2=Paquete,3=Factura,4=Seguro,5=Todos)
 */
export const fetchReportData = createAsyncThunk(
  'transactions/fetchReportData',
  async (
    { from_date, to_date, type = 5, page = 1 }:
    { from_date: string; to_date: string; type?: number; page?: number },
    { rejectWithValue }
  ) => {
    try {
      const res = await reportService.getReportData(from_date, to_date, type, page);
      if (!res.success) throw new Error(res.message || 'Error al obtener reporte');
      return { ...res.data, page };
    } catch (err: any) {
      return rejectWithValue(err.message || 'Error al conectar con el servidor');
    }
  }
);

export const reverseTransaction = createAsyncThunk(
  'transactions/reverseTransaction',
  async (id: string, { rejectWithValue, dispatch }) => {
    try {
      if (!id) return rejectWithValue('ID de transacción inválido o vacío');
      const response = await operationsService.reverseRecharge(id);
      if (response && response.success) {
        try { await dispatch(fetchLastMovements()).unwrap(); } catch { /* ok */ }
        return { success: true, id };
      }
      return rejectWithValue(response?.message || 'Error al revertir la transacción');
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data.message || 'Error al revertir la transacción');
      }
      return rejectWithValue((error as Error).message || 'Error al conectar con el servidor');
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const transactionsSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    setFilter: (state, action: PayloadAction<Partial<TransactionsState['filter']>>) => {
      state.filter = { ...state.filter, ...action.payload };
      state.filteredTransactions = applyFilter(state.transactions, state.filter);
    },
    clearFilter: (state) => {
      state.filter = {};
      state.filteredTransactions = state.transactions;
    },
    clearError: (state) => {
      state.error = null;
    },
    addTransaction: (state, action: PayloadAction<Transaction>) => {
      state.transactions = [action.payload, ...state.transactions];
      state.filteredTransactions = applyFilter(state.transactions, state.filter);
    },
    updateTransaction: (state, action: PayloadAction<{
      id: string;
      status: Transaction['status'];
      relatedTransactionId?: string;
    }>) => {
      const index = state.transactions.findIndex(t => t.id === action.payload.id);
      if (index !== -1) {
        state.transactions[index] = {
          ...state.transactions[index],
          status: action.payload.status,
          relatedTransactionId: action.payload.relatedTransactionId,
        };
        state.filteredTransactions = applyFilter(state.transactions, state.filter);
      }
    },
  },
  extraReducers: (builder) => {
    // fetchTransactions
    builder.addCase(fetchTransactions.pending, (state) => { state.isLoading = true; state.error = null; });
    builder.addCase(fetchTransactions.fulfilled, (state, action) => {
      state.isLoading = false;
      state.transactions = action.payload;
      state.filteredTransactions = applyFilter(action.payload, state.filter);
    });
    builder.addCase(fetchTransactions.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // fetchTransactionById
    builder.addCase(fetchTransactionById.pending, (state) => { state.isLoading = true; state.error = null; });
    builder.addCase(fetchTransactionById.fulfilled, (state, action) => { state.isLoading = false; state.currentTransaction = action.payload; });
    builder.addCase(fetchTransactionById.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; });

    // fetchLastMovements
    builder.addCase(fetchLastMovements.pending, (state) => { state.error = null; });
    builder.addCase(fetchLastMovements.fulfilled, (state, action) => { state.lastMovements = action.payload; });
    builder.addCase(fetchLastMovements.rejected, (state, action) => { state.error = action.payload as string; });

    // fetchReportData
    builder.addCase(fetchReportData.pending, (state) => { state.isLoadingReport = true; state.error = null; });
    builder.addCase(fetchReportData.fulfilled, (state, action) => {
      state.isLoadingReport = false;
      const payload = action.payload as any;
      // Soporta tanto { data: [...] } como { transactions: [...] } o el array directamente
      const rows: any[] =
        Array.isArray(payload?.data)         ? payload.data         :
        Array.isArray(payload?.transactions)  ? payload.transactions :
        Array.isArray(payload)               ? payload              : [];

      state.reportData      = payload?.page === 1 ? rows : [...state.reportData, ...rows];
      state.reportPage      = payload?.current_page  ?? 1;
      state.reportLastPage  = payload?.last_page     ?? 1;
      state.reportTotals    = {
        solds:      payload?.totals_solds      ?? 0,
        reverse:    payload?.total_reverse     ?? 0,
        error:      payload?.total_error       ?? 0,
        amount:     payload?.total_amount      ?? '0',
        commission: payload?.total_commission  ?? '0',
      };
    });
    builder.addCase(fetchReportData.rejected, (state, action) => {
      state.isLoadingReport = false;
      state.error = action.payload as string;
      // Reset pagination para evitar que 'Cargar más' cause duplicados
      state.reportPage     = 1;
      state.reportLastPage = 1;
    });

    // reverseTransaction
    builder.addCase(reverseTransaction.pending, (state) => { state.error = null; });
    builder.addCase(reverseTransaction.rejected, (state, action) => { state.error = action.payload as string; });
  },
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

const applyFilter = (transactions: Transaction[], filter: TransactionsState['filter']) => {
  let filtered = [...transactions];
  if (filter.type)        filtered = filtered.filter(t => t.type === filter.type);
  if (filter.provider)    filtered = filtered.filter(t => t.provider === filter.provider);
  if (filter.phoneNumber) {
    filtered = filtered.filter(t => {
      switch (t.type) {
        case 'RECARGA':  return (t.details as any).phoneNumber?.includes(filter.phoneNumber);
        case 'PAQUETE':  return (t.details as any).phoneNumber?.includes(filter.phoneNumber);
        case 'SEGURO':   return (t.details as any).customerPhone?.includes(filter.phoneNumber);
        default:         return false;
      }
    });
  }
  if (filter.startDate) filtered = filtered.filter(t => new Date(t.date) >= new Date(filter.startDate!));
  if (filter.endDate)   filtered = filtered.filter(t => new Date(t.date) <= new Date(filter.endDate!));
  return filtered;
};

export const { setFilter, clearFilter, clearError, addTransaction, updateTransaction } = transactionsSlice.actions;
export default transactionsSlice.reducer;
